import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ArrowLeft, FileText, DollarSign, User, Calendar, Building2, AlertCircle, Download, Eye, Image, File, Send, Loader2 } from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentClaimDetails() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [claim, setClaim] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({ status: "", notes: "" });
  const [payment, setPayment] = useState({ amount: "", check_number: "", notes: "" });
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [processingPayout, setProcessingPayout] = useState(null);

  useEffect(() => {
    fetchClaim();
    fetchPayoutStatus();
  }, [claimId]);

  const fetchClaim = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/claims/${claimId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaim(response.data);
    } catch (error) {
      toast.error("Failed to load claim details");
      navigate("/agent/claims");
    } finally {
      setLoading(false);
    }
  };

  const fetchPayoutStatus = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/payouts/status/${claimId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayoutStatus(response.data);
    } catch (error) {
      // Silently fail - payout status is optional info
    }
  };

  const handleTriggerPayout = async (paymentId) => {
    const token = localStorage.getItem("agent_token");
    setProcessingPayout(paymentId || "claim");
    try {
      const response = await axios.post(`${API}/agent/payouts/process-claim/${claimId}`, 
        { payment_id: paymentId || null, notes: "Manual payout trigger from admin" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Payout initiated! Status: ${response.data.status}. Amount: $${response.data.amount?.toFixed(2)}`);
      fetchPayoutStatus();
      fetchClaim();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payout failed");
    } finally {
      setProcessingPayout(null);
    }
  };

  const handleStatusUpdate = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/agent/claims/${claimId}/status`, statusUpdate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Claim status updated");
      setStatusDialogOpen(false);
      fetchClaim();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handlePaymentIssue = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/agent/claims/${claimId}/payment`, {
        amount: parseFloat(payment.amount),
        month_number: parseInt(payment.month_number) || 1,
        notes: payment.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payoutInfo = response.data?.stripe_payout;
      if (payoutInfo?.initiated) {
        toast.success(`Payment issued & payout initiated via Stripe! Status: ${payoutInfo.status}`);
      } else {
        toast.success("Payment issued successfully (added to wallet)");
      }
      setPaymentDialogOpen(false);
      setPayment({ amount: "", month_number: "", notes: "" });
      fetchClaim();
      fetchPayoutStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to issue payment");
    }
  };

  const handlePreviewDocument = async (documentId) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/claims/${claimId}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewDoc(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load document preview");
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/claims/${claimId}/documents/${documentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Document downloaded");
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith("image/")) return <Image className="h-5 w-5 text-blue-500" />;
    if (contentType?.includes("pdf")) return <FileText className="h-5 w-5 text-red-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      under_review: "bg-purple-100 text-purple-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      paid: "bg-emerald-100 text-emerald-800",
      closed: "bg-gray-100 text-gray-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const getPayoutStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      in_transit: "bg-blue-100 text-blue-800",
      paid: "bg-emerald-100 text-emerald-800",
      failed: "bg-red-100 text-red-800",
      canceled: "bg-gray-100 text-gray-800",
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <AgentLayout title="Claim Details">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  if (!claim) return null;

  return (
    <AgentLayout title="Claim Details">
      <Button 
        variant="ghost" 
        className="mb-6"
        onClick={() => navigate("/agent/claims")}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Claims
      </Button>

      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
            Claim Details
          </h1>
          <p className="font-mono text-sm text-muted-foreground">{claim.claim_id}</p>
        </div>
        <Badge className={`${getStatusBadge(claim.status)} text-sm px-4 py-2`}>
          {claim.status.replace('_', ' ').toUpperCase()}
        </Badge>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Customer Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-primary/10 p-3 rounded-full">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{claim.user?.full_name || "N/A"}</h3>
                  <p className="text-muted-foreground">{claim.user?.email}</p>
                  {claim.user?.phone && <p className="text-muted-foreground">{claim.user?.phone}</p>}
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Policy ID</p>
                  <p className="font-mono">{claim.policy_id}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Monthly Rent</p>
                  <p className="font-semibold">${claim.policy?.monthly_rent || "N/A"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Claim Details */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Claim Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="bg-muted p-2 rounded-lg">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Job Loss Date</p>
                    <p className="font-medium">{new Date(claim.job_loss_date).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-muted p-2 rounded-lg">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Last Employer</p>
                    <p className="font-medium">{claim.last_employer}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-2">Reason for Job Loss</p>
                <p className="bg-muted p-4 rounded-lg text-sm">{claim.reason_for_job_loss}</p>
              </div>
            </CardContent>
          </Card>

          {/* Documents */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Uploaded Documents</CardTitle>
            </CardHeader>
            <CardContent>
              {claim.documents && claim.documents.length > 0 ? (
                <div className="space-y-2">
                  {claim.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-muted rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        {getFileIcon(doc.content_type)}
                        <div>
                          <p className="text-sm font-medium">{doc.filename}</p>
                          <p className="text-xs text-muted-foreground">
                            {(doc.size / 1024).toFixed(1)} KB • {new Date(doc.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {doc.content_type?.startsWith("image/") && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handlePreviewDocument(doc.document_id)}
                            data-testid={`preview-doc-${doc.document_id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Preview
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDownloadDocument(doc.document_id, doc.filename)}
                          data-testid={`download-doc-${doc.document_id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">No documents uploaded</p>
              )}
            </CardContent>
          </Card>

          {/* Payments */}
          {claim.payments && claim.payments.length > 0 && (
            <Card className="card-organic">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Payment History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {claim.payments.map((pmt, index) => (
                    <div key={index} className="flex items-center justify-between border-b border-border pb-3 last:border-0 last:pb-0">
                      <div className="flex items-center gap-3">
                        <div className="bg-green-100 p-2 rounded-full">
                          <DollarSign className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold">${pmt.amount.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">
                            Month {pmt.month_number} of 3
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <div>
                          <Badge className="bg-green-100 text-green-800">{pmt.status}</Badge>
                          {pmt.payout_status && (
                            <Badge className={`ml-1 ${getPayoutStatusBadge(pmt.payout_status)}`}>
                              Payout: {pmt.payout_status}
                            </Badge>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(pmt.issued_at).toLocaleDateString()}
                          </p>
                        </div>
                        {!pmt.payout_status && payoutStatus?.user_has_connect && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="ml-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => handleTriggerPayout(pmt.payment_id)}
                            disabled={processingPayout === pmt.payment_id}
                            data-testid={`trigger-payout-${pmt.payment_id}`}
                          >
                            {processingPayout === pmt.payment_id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <><Send className="h-3.5 w-3.5 mr-1" /> Send Payout</>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Actions Sidebar */}
        <div className="space-y-6">
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => {
                  setStatusUpdate({ status: claim.status, notes: "" });
                  setStatusDialogOpen(true);
                }}
                data-testid="update-status-btn"
              >
                <AlertCircle className="h-4 w-4 mr-2" />
                Update Status
              </Button>

              {(claim.status === "approved" || claim.status === "partially_paid") && (
                <Button 
                  className="w-full btn-primary"
                  onClick={() => setPaymentDialogOpen(true)}
                  data-testid="issue-payment-btn"
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Issue Payment
                </Button>
              )}

              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => navigate(`/agent/customers/${claim.user_id}`)}
              >
                <User className="h-4 w-4 mr-2" />
                View Customer
              </Button>
            </CardContent>
          </Card>

          {/* Payout Status Card */}
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Payout Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {payoutStatus?.user_has_connect ? (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
                    <span className="text-emerald-700 font-medium">Bank Account Connected</span>
                  </div>
                  
                  {payoutStatus?.claim_level_payout?.payout_status && (
                    <div className="bg-muted p-3 rounded-lg text-sm space-y-1">
                      <p className="font-medium">Claim Payout</p>
                      <Badge className={getPayoutStatusBadge(payoutStatus.claim_level_payout.payout_status)}>
                        {payoutStatus.claim_level_payout.payout_status}
                      </Badge>
                    </div>
                  )}

                  {payoutStatus?.payout_logs?.length > 0 && (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      <p className="text-xs font-medium text-muted-foreground">Recent Payout Activity</p>
                      {payoutStatus.payout_logs.slice(0, 5).map((log, idx) => (
                        <div key={idx} className="bg-muted/50 p-2 rounded text-xs">
                          <div className="flex justify-between">
                            <span className="font-medium">${log.amount?.toFixed(2)}</span>
                            <Badge className={`text-[10px] ${getPayoutStatusBadge(log.payout_status)}`}>
                              {log.payout_status}
                            </Badge>
                          </div>
                          <p className="text-muted-foreground mt-0.5">
                            {new Date(log.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {claim.status === "approved" && !payoutStatus?.claim_level_payout?.payout_status && (
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => handleTriggerPayout(null)}
                      disabled={processingPayout === "claim"}
                      data-testid="send-full-payout-btn"
                    >
                      {processingPayout === "claim" ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send Full Payout
                    </Button>
                  )}
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                    <span className="text-yellow-700 font-medium">No Bank Account Linked</span>
                  </div>
                  <p className="text-xs">
                    Customer has not connected their bank account via Stripe Connect. 
                    Payments will be added to their wallet for manual withdrawal.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {claim.notes && claim.notes.length > 0 && (
            <Card className="card-organic">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {claim.notes.map((note, index) => (
                    <div key={index} className="bg-muted p-3 rounded-lg text-sm">
                      <p>{note.note}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(note.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>Update Claim Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select 
                value={statusUpdate.status} 
                onValueChange={(value) => setStatusUpdate({ ...statusUpdate, status: value })}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={statusUpdate.notes}
                onChange={(e) => setStatusUpdate({ ...statusUpdate, notes: e.target.value })}
                placeholder="Add any notes about this status change..."
                rows={3}
                data-testid="status-notes-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleStatusUpdate} data-testid="confirm-status-btn">Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>Issue Claim Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {payoutStatus?.user_has_connect && (
              <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg text-sm text-emerald-800">
                <p className="font-medium">Direct Payout Enabled</p>
                <p className="text-xs mt-1">This payment will be automatically sent to the customer&apos;s bank account via Stripe.</p>
              </div>
            )}
            {!payoutStatus?.user_has_connect && (
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-sm text-yellow-800">
                <p className="font-medium">Wallet-Only Payment</p>
                <p className="text-xs mt-1">Customer has not set up direct payouts. Funds will be added to their wallet for manual withdrawal.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={payment.amount}
                onChange={(e) => setPayment({ ...payment, amount: e.target.value })}
                placeholder={claim.policy?.monthly_rent?.toString() || "0.00"}
                data-testid="payment-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Month (1-3)</Label>
              <Input
                type="number"
                min="1"
                max="3"
                value={payment.month_number}
                onChange={(e) => setPayment({ ...payment, month_number: e.target.value })}
                placeholder="1"
                data-testid="month-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={payment.notes}
                onChange={(e) => setPayment({ ...payment, notes: e.target.value })}
                placeholder="Add any payment notes..."
                rows={2}
                data-testid="payment-notes-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handlePaymentIssue} 
              disabled={!payment.amount || !payment.month_number}
              data-testid="confirm-payment-btn"
            >
              {payoutStatus?.user_has_connect ? "Issue & Send Payout" : "Issue Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>Document Preview</DialogTitle>
          </DialogHeader>
          {previewDoc && (
            <div className="space-y-4">
              <div className="bg-muted p-3 rounded-lg">
                <p className="font-medium">{previewDoc.filename}</p>
                <p className="text-sm text-muted-foreground">
                  {previewDoc.content_type} • {(previewDoc.size / 1024).toFixed(1)} KB
                </p>
              </div>
              {previewDoc.content_type?.startsWith("image/") && previewDoc.data && (
                <div className="flex justify-center">
                  <img 
                    src={`data:${previewDoc.content_type};base64,${previewDoc.data}`}
                    alt={previewDoc.filename}
                    className="max-w-full max-h-[500px] rounded-lg border"
                  />
                </div>
              )}
              <DialogFooter>
                <Button 
                  onClick={() => handleDownloadDocument(previewDoc.document_id, previewDoc.filename)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
